import { type KeyboardEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Network,
  PauseCircle,
  Plus,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiClient, type Job, type Validation } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import type { Network as NetworkType } from '@/types/network'

type OverviewJob = Job & {
  networkName: string
  networkId: string
}

type OverviewValidation = Validation & {
  networkName: string
  networkId: string
  jobCreatedAt?: string
}

type NetworkStateTone = 'success' | 'warning' | 'danger' | 'default'

const toneClassName: Record<NetworkStateTone, string> = {
  success: 'text-emerald-700',
  warning: 'text-amber-700',
  danger: 'text-red-600',
  default: 'text-muted-foreground',
}

const toneBadgeClassName: Record<NetworkStateTone, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  danger: 'border-red-200 bg-red-50 text-red-700',
  default: 'border-border/60 bg-background text-muted-foreground',
}

function formatRelativeTime(value?: string | null) {
  if (!value) return 'Never'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Never'

  const diffMs = date.getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / (1000 * 60))
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (Math.abs(diffMinutes) < 60) return formatter.format(diffMinutes, 'minute')

  const diffHours = Math.round(diffMinutes / 60)
  if (Math.abs(diffHours) < 48) return formatter.format(diffHours, 'hour')

  return formatter.format(Math.round(diffHours / 24), 'day')
}

function formatValidationType(type?: string) {
  if (type === 'analysis') return 'Analysis'
  return 'Validation'
}

function formatShortId(value: string) {
  return value.slice(-4).toUpperCase()
}

function getValidationActivityAt(validation?: OverviewValidation | null) {
  if (!validation) return null
  return validation.completedAt || validation.startedAt || validation.createdAt || validation.jobCreatedAt || null
}

function handleRowKeyDown(event: KeyboardEvent<HTMLElement>, onSelect: () => void) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    onSelect()
  }
}

function getResolvedValidationOutcome(validation?: OverviewValidation | null) {
  if (!validation) return null
  if (['running', 'pending', 'queued'].includes(validation.status)) return null
  if (validation.result === 'failure' || ['errored', 'failed', 'failure', 'error'].includes(validation.status)) return 'failure' as const
  if (validation.result === 'success' || ['success', 'completed', 'passed'].includes(validation.status)) return 'success' as const
  return null
}

function getValidationStatusMeta(validation: OverviewValidation) {
  if (['running', 'pending', 'queued'].includes(validation.status)) {
    return { label: 'In progress', tone: 'warning' as const, icon: Clock3 }
  }
  if (getResolvedValidationOutcome(validation) === 'failure') {
    return { label: 'Failed', tone: 'danger' as const, icon: AlertCircle }
  }
  if (getResolvedValidationOutcome(validation) === 'success') {
    return { label: 'Passed', tone: 'success' as const, icon: CheckCircle2 }
  }
  return { label: 'Queued', tone: 'default' as const, icon: Clock3 }
}

function getNetworkState(network: NetworkType, latestResolvedValidation?: OverviewValidation | null) {
  if (!network.active) {
    return { label: 'Inactive', tone: 'default' as NetworkStateTone, icon: PauseCircle }
  }
  if (!network.lastValidatedAt) {
    return { label: 'Needs run', tone: 'warning' as const, icon: Clock3 }
  }
  if (getResolvedValidationOutcome(latestResolvedValidation) === 'failure') {
    return { label: 'Failed', tone: 'danger' as const, icon: AlertCircle }
  }
  return { label: 'Passing', tone: 'success' as const, icon: CheckCircle2 }
}

function getLatestResolvedValidationsByNetwork(validations: OverviewValidation[]) {
  const latest = new Map<string, OverviewValidation>()
  for (const validation of validations) {
    if (latest.has(validation.networkId) || !getResolvedValidationOutcome(validation)) continue
    latest.set(validation.networkId, validation)
  }
  return latest
}

type AttentionItem = {
  id: string
  href: string
  title: string
  detail: string
  statusLabel: string
  tone: 'warning' | 'danger'
}

function buildAttentionQueue(
  networks: NetworkType[],
  latestResolved: Map<string, OverviewValidation>,
): AttentionItem[] {
  const failed: AttentionItem[] = Array.from(latestResolved.values())
    .filter((v) => getResolvedValidationOutcome(v) === 'failure')
    .map((v) => ({
      id: `v-${v.id}`,
      href: `/validations/${v.id}`,
      title: v.networkName,
      detail: `${formatValidationType(v.type)} failed ${formatRelativeTime(getValidationActivityAt(v))}`,
      statusLabel: 'Failed',
      tone: 'danger' as const,
    }))

  const needsRun: AttentionItem[] = networks
    .filter((n) => n.active && !n.lastValidatedAt)
    .map((n) => ({
      id: `n-${n.id}`,
      href: `/networks/${n.id}`,
      title: n.name,
      detail: 'Ready for first validation',
      statusLabel: 'Needs run',
      tone: 'warning' as const,
    }))

  return [...failed, ...needsRun].slice(0, 5)
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const [networks, setNetworks] = useState<NetworkType[]>([])
  const [jobs, setJobs] = useState<OverviewJob[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const networksResp = await apiClient.listNetworks()
        const nets = networksResp.success ? networksResp.data?.networks ?? [] : []

        const jobResponses = await Promise.all(
          nets.map(async (network) => {
            const resp = await apiClient.getNetworkJobs(network.id)
            return (resp.success ? resp.data?.jobs ?? [] : []).map((job) => ({
              ...job,
              networkId: network.id,
              networkName: network.name,
            }))
          }),
        )

        setNetworks(nets)
        setJobs(
          jobResponses
            .flat()
            .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()),
        )
      } catch (err) {
        console.error('Failed to load overview:', err)
        setError('Failed to load overview')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [])

  const allValidations: OverviewValidation[] = jobs.flatMap((job) =>
    (job.validations ?? []).map((v) => ({
      ...v,
      networkId: job.networkId,
      networkName: job.networkName,
      jobCreatedAt: job.createdAt,
    })),
  )
  const sortedValidations = [...allValidations].sort(
    (a, b) => new Date(getValidationActivityAt(b) || 0).getTime() - new Date(getValidationActivityAt(a) || 0).getTime(),
  )
  const latestResolved = getLatestResolvedValidationsByNetwork(sortedValidations)
  const attentionQueue = buildAttentionQueue(networks, latestResolved)

  const activeCount = networks.filter((n) => n.active).length
  const passingCount = networks.filter((n) => {
    const state = getNetworkState(n, latestResolved.get(n.id))
    return state.tone === 'success'
  }).length
  const attentionCount = attentionQueue.length

  const recentActivity = sortedValidations.slice(0, 4)

  if (isLoading) {
    return (
      <div className="flex min-h-[18rem] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <Button size="sm" onClick={() => navigate(networks.length > 0 ? `/networks/${networks[0].id}` : '/networks/add')}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {networks.length > 0 ? 'New Validation' : 'Add Network'}
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="app-surface">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Networks</CardTitle>
            <Network className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{networks.length}</div>
          </CardContent>
        </Card>
        <Card className="app-surface">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{activeCount}</div>
          </CardContent>
        </Card>
        <Card className="app-surface">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Passing</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums text-emerald-700">{passingCount}</div>
          </CardContent>
        </Card>
        <Card className="app-surface">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Needs Attention</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className={cn('text-2xl font-bold tabular-nums', attentionCount > 0 ? 'text-amber-700' : '')}>{attentionCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Networks table */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Networks</h2>
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/networks')}>
            View all <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
        <div className="app-surface rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 hidden sm:table-cell">Source</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 hidden sm:table-cell">Last Validated</th>
              </tr>
            </thead>
            <tbody>
              {networks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                    No networks yet.{' '}
                    <button className="text-primary hover:underline" onClick={() => navigate('/networks/add')}>
                      Add your first network
                    </button>
                  </td>
                </tr>
              ) : (
                networks.map((network) => {
                  const state = getNetworkState(network, latestResolved.get(network.id))
                  const StateIcon = state.icon
                  return (
                    <tr
                      key={network.id}
                      className="border-b last:border-0 cursor-pointer transition-colors hover:bg-muted/50"
                      onClick={() => navigate(`/networks/${network.id}`)}
                      onKeyDown={(e) => handleRowKeyDown(e, () => navigate(`/networks/${network.id}`))}
                      tabIndex={0}
                      role="button"
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium">{network.name}</span>
                        {network.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 max-w-sm truncate">{network.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <Badge variant="outline" className="text-[10px] capitalize">{network.sourceType}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <StateIcon className={cn('h-3.5 w-3.5', toneClassName[state.tone])} />
                          <Badge variant="outline" className={cn('text-[10px]', toneBadgeClassName[state.tone])}>
                            {state.label}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground tabular-nums text-xs hidden sm:table-cell">
                        {formatRelativeTime(network.lastValidatedAt)}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom row: Attention + Recent Activity side by side */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Attention Required */}
        <div>
          <div className="mb-3 flex items-center gap-2">
            <h2 className="text-base font-semibold">Attention Required</h2>
            {attentionCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">
                {attentionCount}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {attentionQueue.length === 0 ? (
              <div className="app-surface rounded-lg px-4 py-6 text-center text-sm text-muted-foreground">
                All clear — no issues need attention.
              </div>
            ) : (
              attentionQueue.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="app-surface w-full rounded-lg px-4 py-3 text-left transition-colors hover:bg-muted/50"
                  onClick={() => navigate(item.href)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <Badge variant="outline" className={cn('shrink-0 text-[10px]', toneBadgeClassName[item.tone])}>
                      {item.statusLabel}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold">Recent Activity</h2>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/validations')}>
              View all <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
          <div className="space-y-2">
            {recentActivity.length === 0 ? (
              <div className="app-surface rounded-lg px-4 py-6 text-center text-sm text-muted-foreground">
                No validation activity yet.
              </div>
            ) : (
              recentActivity.map((validation) => {
                const status = getValidationStatusMeta(validation)
                const StatusIcon = status.icon
                return (
                  <button
                    key={validation.id}
                    type="button"
                    className="app-surface w-full rounded-lg px-4 py-3 text-left transition-colors hover:bg-muted/50"
                    onClick={() => navigate(`/validations/${validation.id}`)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <StatusIcon className={cn('h-3.5 w-3.5 shrink-0', toneClassName[status.tone])} />
                        <span className="text-sm font-medium truncate">
                          {validation.networkName}
                          <span className="mx-1 font-normal text-muted-foreground">/</span>
                          <span className="font-normal">{formatShortId(validation.id)}</span>
                        </span>
                      </div>
                      <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
                        {formatRelativeTime(getValidationActivityAt(validation))}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground ml-5.5">
                      {formatValidationType(validation.type)} — {status.label}
                    </p>
                  </button>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
